import type { Column } from "@tanstack/react-table"
import { cn } from "../../lib/utils"
import { Icon } from "../Icon/Icon"

export interface SortableHeaderProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  /**
   * The content to display in the header (e.g., column name)
   */
  children: React.ReactNode
  /**
   * The TanStack Table column object
   */

  // biome-ignore lint/suspicious/noExplicitAny: needed
  column: Column<any, any>
}

export const SortableHeader = ({ className, children, column, ...props }: SortableHeaderProps) => {
  const handleClick = () => {
    const currentSort = column.getIsSorted()

    const getNextSortState = (current: false | "asc" | "desc") => {
      switch (current) {
        case false:
          return "asc"
        case "asc":
          return "desc"
        case "desc":
          return false
      }
    }

    const nextState = getNextSortState(currentSort)

    if (nextState === false) {
      column.clearSorting()
    } else {
      column.toggleSorting(nextState === "desc")
    }
  }

  return (
    <button
      className={cn(
        "flex h-auto items-center justify-start p-0 text-14 font-500",
        "text-root-text-color-subtle hover:bg-action-ghost-background-hover",
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
      {getSortIcon(column.getIsSorted())}
    </button>
  )
}

SortableHeader.displayName = "SortableHeader"

const iconMap = {
  asc: <Icon name="SortUp" className="ml-2 text-root-icon-color-base" />,
  desc: <Icon name="SortDown" className="ml-2 text-root-icon-color-base" />,
} as const

function getSortIcon(sortState: false | "asc" | "desc") {
  return sortState ? (
    iconMap[sortState]
  ) : (
    <Icon name="Sort" className="ml-2 text-root-icon-color-subtle" />
  )
}
